import type { TwoFactorChallenge } from '@nucleus-commerce/api-client/platform';
import { ApiError } from '@nucleus-commerce/api-client/transport';

export type CodeMethod = 'totp' | 'recovery_code';

/** Warn once this few recovery codes remain. */
export const RECOVERY_CODES_LOW = 2;

export function isTwoFactorChallenge(result: object): result is TwoFactorChallenge {
  return 'challengeToken' in result;
}

export function recoveryCodesLow(left: number): boolean {
  return left <= RECOVERY_CODES_LOW;
}

/** Whole seconds until `iso`, never negative; an unparsable date counts as already passed. */
export function secondsUntil(iso: string, now: number): number {
  const deadline = Date.parse(iso);
  if (Number.isNaN(deadline)) return 0;
  return Math.max(0, Math.ceil((deadline - now) / 1000));
}

/** `4:05` */
export function formatCountdown(seconds: number): string {
  const safe = Math.max(0, Math.floor(seconds));
  return `${Math.floor(safe / 60)}:${String(safe % 60).padStart(2, '0')}`;
}

/** How a challenge ended before a code verified: its time ran out, or it no longer verifies. */
export type ChallengeEnd = 'expired' | 'invalid';

/**
 * Reads a failed verify by its code: 401 TOKEN_EXPIRED is a challenge past its deadline,
 * 401 TOKEN_INVALID one that no longer verifies (tampered, or issued for another surface), any
 * other 401 a wrong code. Anything else is not a verify failure this step can explain.
 */
export function verifyFailure(error: unknown): ChallengeEnd | 'wrong_code' | null {
  if (!(error instanceof ApiError) || error.kind !== 'unauthorized') return null;
  if (error.code === 'TOKEN_EXPIRED') return 'expired';
  if (error.code === 'TOKEN_INVALID') return 'invalid';
  return 'wrong_code';
}

/** Which form fields a 422 from /auth/2fa/enable, /recovery-codes or /disable points at. */
export function twoFactorFieldErrors(error: unknown): Array<'code' | 'password'> {
  if (!(error instanceof ApiError)) return [];
  const fields: Array<'code' | 'password'> = [];
  if (error.fieldErrors['body.code']) fields.push('code');
  if (error.fieldErrors['body.password']) fields.push('password');
  return fields;
}
